import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Home, ChevronRight, Download, Printer, FileX } from 'lucide-react'
import { getDocument, getPermissions, downloadDocument } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import LoadingSkeleton from '../../components/shared/LoadingSkeleton'
import PageHeader from '../../components/ui/PageHeader'
import EmptyState from '../../components/ui/EmptyState'
import ContextualTip from '../../components/ui/ContextualTip'
import HelpPopover from '../../components/help/HelpPopover'

export default function SharedDocumentView() {
  const { documentId } = useParams<{ documentId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()

  const { data: document, isLoading } = useQuery({
    queryKey: ['shared-document', documentId],
    queryFn: () => getDocument(documentId!),
    enabled: !!documentId,
  })

  const { data: permissions } = useQuery({
    queryKey: ['shared-permissions', documentId],
    queryFn: () => getPermissions(documentId!),
    enabled: !!documentId,
  })

  const myPermission = permissions?.find((p) => p.grantee_id === user?.id)

  const [actionError, setActionError] = useState('')
  const [downloading, setDownloading] = useState(false)
  const [printing, setPrinting] = useState(false)

  const handleDownload = async () => {
    if (!documentId) return
    setActionError('')
    setDownloading(true)
    try {
      const url = await downloadDocument(documentId)
      // `document` is shadowed by the query data in this component, so reach the
      // DOM through `window.document`. An anchor pointed at the presigned S3 URL
      // honours the object's Content-Disposition and downloads without navigating
      // away from the SPA.
      const a = window.document.createElement('a')
      a.href = url
      a.rel = 'noopener'
      a.download = document?.filename || document?.title || 'document'
      window.document.body.appendChild(a)
      a.click()
      window.document.body.removeChild(a)
    } catch {
      setActionError('Download failed. Please try again.')
    } finally {
      setDownloading(false)
    }
  }

  const handlePrint = async () => {
    if (!documentId) return
    setActionError('')
    // Open the print tab synchronously inside the click gesture — browsers block
    // window.open() that runs after an await boundary.
    const printWindow = window.open('', '_blank')
    setPrinting(true)
    try {
      const url = await downloadDocument(documentId)
      if (printWindow) {
        printWindow.location.href = url
      } else {
        setActionError('Unable to open a print view. Please allow pop-ups and try again.')
      }
    } catch {
      if (printWindow) printWindow.close()
      setActionError('Print failed. Please try again.')
    } finally {
      setPrinting(false)
    }
  }

  if (isLoading) return <LoadingSkeleton rows={1} />
  if (!document) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
          <button onClick={() => navigate('/shared')} className="hover:text-iflow-600 flex items-center gap-1">
            <Home size={14} /> Shared
          </button>
        </div>
        <EmptyState
          icon={FileX}
          title="Document not available"
          description="This document may have been removed, or it isn't shared at your disclosure level."
          action={
            <button
              onClick={() => navigate('/shared')}
              className="px-5 py-2.5 bg-iflow-700 text-white text-sm font-medium rounded-lg hover:bg-iflow-800 transition-colors"
            >
              Back to Shared with me
            </button>
          }
        />
      </div>
    )
  }

  // P7 — honest controls. Every action button is rendered so the collaborator
  // can SEE what exists; when the grant doesn't authorise it the control is
  // disabled with a title explaining why (the disclosure level gates it),
  // rather than silently hidden or faking success.
  const canDownload = !!myPermission?.allow_download
  const canPrint = !!myPermission?.allow_print
  const level = myPermission?.permission_level || 'view'

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
        <button onClick={() => navigate('/shared')} className="hover:text-iflow-600 flex items-center gap-1">
          <Home size={14} /> Shared
        </button>
        <ChevronRight size={14} />
        <span className="text-gray-800 font-medium">{document.title}</span>
      </div>

      <PageHeader
        title={document.title}
        description={document.description}
        helpKey="shared"
        status={{ label: `${level.charAt(0).toUpperCase()}${level.slice(1)} access`, tone: 'info' }}
        primaryAction={
          <button
            onClick={handleDownload}
            disabled={!canDownload || downloading}
            title={canDownload ? undefined : "Downloading isn't enabled for your access level — ask the owner to raise your disclosure level (available at L4)."}
            className="flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-iflow-700 rounded-lg hover:bg-iflow-800 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
          >
            <Download size={14} /> {downloading ? 'Preparing…' : 'Download'}
          </button>
        }
        secondaryActions={
          <button
            onClick={handlePrint}
            disabled={!canPrint || printing}
            title={canPrint ? undefined : "Printing isn't enabled for your access level — ask the owner to raise your disclosure level (available at L4)."}
            className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-700 border border-sky-200 rounded-lg hover:bg-white/50 disabled:cursor-not-allowed disabled:text-slate-400 disabled:hover:bg-transparent"
          >
            <Printer size={14} /> {printing ? 'Preparing…' : 'Print'}
          </button>
        }
      />

      <ContextualTip id="shared-document-ladder" tone="info">
        What you can do with this document — view, download, print — is set by the
        disclosure level you were granted. Controls shown greyed-out aren’t enabled
        at your level yet.{' '}
        <HelpPopover topic="concept-disclosure-ladder" size={14} />
      </ContextualTip>

      {actionError && <p role="alert" className="mt-3 text-sm text-red-600">{actionError}</p>}

      <div className="light-card p-6 mt-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className="text-xs text-gray-500 block">Type</span>
            <span className="text-sm font-medium">{document.content_type}</span>
          </div>
          <div>
            <span className="text-xs text-gray-500 block">Filename</span>
            <span className="text-sm font-medium truncate">{document.filename || document.s3_key?.split('/').pop() || '—'}</span>
          </div>
          <div>
            <span className="text-xs text-gray-500 block">Permission</span>
            <span className="text-sm font-medium capitalize">{level}</span>
          </div>
          <div>
            <span className="text-xs text-gray-500 block">Uploaded</span>
            <span className="text-sm font-medium">{new Date(document.created_at).toLocaleDateString()}</span>
          </div>
        </div>
      </div>

      {document.topics && document.topics.length > 0 && (
        <div className="light-card p-6 mt-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Topics</h3>
          <div className="flex flex-wrap gap-2">
            {document.topics.map((topic) => (
              <span key={topic} className="px-3 py-1 bg-iflow-50 text-iflow-700 text-xs font-medium rounded-full">
                {topic}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
